/**
 * Class is responsible for changing views by manipulating URL params.
 */
import _ from 'lodash';
import { RepoItem, ZmCollection } from './autogenerated/ZoomBridge';
import Store from './store/Store';
import {
  COLLECTION_ASSETS,
  COLLECTIONS,
  GLOBAL_SEARCH_INFO,
  PWD,
  ASSETLIST_PAGE_NUMBER,
  MODE,
  ALL_FILES_OPTION,
  SHOW_FOLDERNAV_VIEW,
  BREADCRUMB_ASSETS
} from './store/Constants';
import System from './basic/System';
import SaveSettings from './queries/settings/SaveSettings';
import { getRemotePathFromBreadcrumb } from './store/StoreUtil';
import t from './i18n/I18n';
import { Q, TABS } from './util/Constants';
import { isMobile } from './util/ScreenUtil';

const debug = require('./debug/debug')('wc:ViewUtil');

export const View = {
  HOME: 'HOME',
  ASSET_LIST: 'ASSET_LIST',
  ASSET_DETAIL: 'ASSET_DETAIL',
  COLLECTION_LIST: 'COLLECTION_LIST',
  COLLECTION_DETAIL_ASSET_LIST: 'COLLECTION_DETAIL_ASSET_LIST',
  COLLECTION_DETAIL_ASSET_DETAIL: 'COLLECTION_DETAIL_ASSET_DETAIL'
};

export default class ViewUtil {
  static URL_TREE = {
    activeTab: ['HOME', 'ASSETS', 'COLLECTIONS'],
    ASSETS: [],
    COLLECTIONS: [
      'collectionId', //Collection which is open.
      'collection' //Collection unique object..
    ]
  };

  static switchToTab(name, searchView: boolean = false) {
    const { collection, collectionId, fuid, tv, fnv } = window.ARGS;
    const args = { activeTab: name };

    if (name === 'COLLECTIONS') {
      if (collection && collectionId) {
        args.collectionId = collectionId;
      }
      if (fuid) {
        args.fuid = fuid;
      }
    } else if (name === 'REPORTING') {
      args.rpt = true;
    }

    if (collection) {
      args.collection = collection;
    }

    if (name === 'ASSETS') {
      if (fnv === 0) {
        args.fnv = 0;
      }
      if (tv === 0) {
        args.tv = 0;
      }
    }

    ViewUtil.switchToAssetListView();
    ViewUtil.clearThreadIdCommentId();
    if (!searchView && !window.gs.loadFromBackButton) {
      ViewUtil.setGlobalSearchInactive();
    }
    ViewUtil.setPwd(null);
    ViewUtil.setARGS(args, true);

    ViewUtil.setTitle();
  }

  static setGlobalSearchInactive() {
    const globalSearchInfo = Store.get(GLOBAL_SEARCH_INFO);
    if (globalSearchInfo.active) {
      Store.put(GLOBAL_SEARCH_INFO, { active: false, searchString: '' });
    }
  }

  static getMode() {
    const mode = SaveSettings.get(MODE);
    if (mode && mode.toUpperCase() === 'SEARCH') return 'SEARCH';
    if (mode && mode.toUpperCase() === 'FOLDERNAV') return 'FOLDERNAV';
    if (mode && mode.toUpperCase() === 'TREE') return 'TREE';
    return 'SEARCH';
  }

  static isHomeTabActive() {
    const systemTabs = ['ASSETS', 'COLLECTIONS', 'REPORTING', 'SEARCH', 'SETTINGS', TABS.EXTERNAL_FILES.KEY];
    const mobileTabs = ['ASSETS', 'COLLECTIONS', 'REPORTING', 'SEARCH', 'SETTINGS'];
    return isMobile() ? !_.includes(mobileTabs, window.ARGS.activeTab) : !_.includes(systemTabs, window.ARGS.activeTab);
  }

  static isAssetTabActive() {
    return window.ARGS.activeTab === 'ASSETS';
  }
  static isSettingsTabActive() {
    return window.ARGS.activeTab === 'SETTINGS';
  }

  static isCollectionTabActive() {
    return window.ARGS.activeTab === 'COLLECTIONS';
  }

  static isBrowseExternalTabActive() {
    return window.ARGS.activeTab === TABS.EXTERNAL_FILES.KEY && !isMobile();
  }

  static isCollectionListViewActive() {
    return ViewUtil.isCollectionTabActive() && !window.ARGS.collectionId;
  }

  static isCollectionDetailAssetListViewActive() {
    return ViewUtil.isCollectionTabActive() && !!window.ARGS.collectionId && !window.ARGS.fuid;
  }

  static isCollectionDetailAssetDetailViewActive() {
    return ViewUtil.isCollectionTabActive() && !!window.ARGS.collectionId && !!window.ARGS.fuid;
  }

  static isAssetListViewActive() {
    return !window.ARGS.fuid;
  }

  static isAssetAIViewActive() {
    return window.ARGS.ai;
  }

  static isReportingTabActive() {
    return window.ARGS.activeTab === 'REPORTING';
  }

  static isAssetDetailViewActive() {
    return !!window.ARGS.fuid;
  }

  static getFuid() {
    const { fuid } = window.ARGS;
    return fuid ? parseInt(fuid, 10) : null;
  }

  static isDetailsBlockViewActive() {
    return ViewUtil.isAssetTabActive() || !!window.ARGS.detail;
  }

  /**
   * Method will switch to collections views. Where we display the list of all collections.
   */
  static switchToCollections() {
    ViewUtil.switchToTab('COLLECTIONS');
  }

  static switchToHomeTab() {
    ViewUtil.switchToTab('HOME');
  }

  static switchToAssetsTab() {
    ViewUtil.switchToTab('ASSETS');
  }

  static switchToAssetsTabFolder(pwd: number[]) {
    Store.put(ALL_FILES_OPTION, undefined);
    ViewUtil.setARGS({ activeTab: 'ASSETS', pwd: ViewUtil._pwdArrayToString(pwd) }, true);
    Store.put(PWD, pwd);
  }

  /**
   * Method will switch to collection detail view. Where we display the list of all assets in a collection.
   */
  static switchToCollectionId(collectionId) {
    let args = { ...window.ARGS };
    delete args.fuid;
    delete args.pwd;
    args.activeTab = 'COLLECTIONS';
    args.collectionId = collectionId;
    ViewUtil.setARGS(args, true);

    ViewUtil.setTitle();
  }

  static switchToSetting(currentSetting) {
    let args = { ...window.ARGS };
    args.activeTab = 'SETTINGS';
    args.currentSetting = currentSetting;
    ViewUtil.setARGS(args, true);
  }

  static setARGS(args, replace = false) {
    const activeTab = args['activeTab'];
    const currentActiveTab = window.ARGS['activeTab'];
    // only replace when param replace isn't in args
    if (!replace) {
      if (activeTab && activeTab !== currentActiveTab) {
        replace = true;
      } else {
        replace = false;
      }
    }
    if(args.pwd === '') {
      //do nothing..
    } else {
      window.setARGS(args, replace);
    }
    
    window.render();
  }

  static removeArg(name) {
    const args = { ...window.ARGS };
    delete args[name];
    ViewUtil.setARGS(args, true);
  }

  /**
   * Switch to collection add asset form.
   */
  static switchViewToAddAssetsInCollection(collectionId) {
    window.gs.addAssetsForm = true;
    ViewUtil.setARGS({ activeTab: 'COLLECTIONS', collectionId });
    ViewUtil.setTitle();
  }

  static switchToAssetListView() {
    let args = { ...window.ARGS };
    if (!args.fuid) {
      return;
    }

    window.gs.selectedAssets = [];
    delete args.fuid;
    delete args.cId;
    delete args.tId;
    delete args.dialog;
    window.setARGS(args, true);
    window.render();
    ViewUtil.setTitle();
  }

  static switchToAssetAIView(fuid, ai, enforceAssetsTab = false, replace = false) {
    const args = { fuid, ai };
    if (enforceAssetsTab) {
      args.activeTab = 'ASSETS';
    }
    window.setARGS(args, replace);
    window.render();
    ViewUtil.setTitle();
  }

  static switchToAssetDetailView(fuid, enforceAssetsTab = false, replace = false) {
    const args = { fuid };
    if (enforceAssetsTab) {
      args.activeTab = 'ASSETS';
    }
    window.setARGS(args, replace);
    window.render();
    ViewUtil.setTitle();
  }

  static switchToAdvancedSearch(searchString: string) {
    const args = { activeTab: 'SEARCH' };
    if (searchString) {
      args.q = JSON.stringify({ find: searchString });
    }
    ViewUtil.setARGS(args, true);
    ViewUtil.setTitle();
  }

  static isAdvancedSearchTabActive() {
    return window.ARGS.activeTab === 'SEARCH';
  }

  /**
   * Method tells, if URL has direct collection link property or not.
   * @return {boolean}
   */
  static isDirectLinkToCollection() {
    const args = window.ARGS;
    if (args) {
      const privateLinkId = args['collection'];
      const collectionId = args['collectionId'];
      return _.size(privateLinkId) > 0 && collectionId && parseInt(collectionId) > 0;
    }
    return false;
  }

  /**
   * Returns the collection id from window.ARGS
   * @return {number}
   */
  static getCollectionIdFromArgs() {
    const args = window.ARGS;
    if (args && args['collectionId']) {
      const collectionId = args['collectionId'];
      const cId = parseInt(collectionId, 10);
      return isNaN(cId) ? undefined : cId;
    }
    return null;
  }

  /**
   * It is assumed that, you call this method after calling isDirectLinkToCollection.
   * Method will return two items in array. First is privateLinkId(string) and second is collectionId(number)
   * @return {Array}
   */
  static getLinkDetails() {
    const args = window.ARGS;
    if (args) {
      const privateLinkId = args['collection'];
      const collectionId = args['collectionId'];
      return [privateLinkId, collectionId];
    }
    return undefined;
  }

  static showBottomLeftTip(message, dismissDelay = 0) {
    window.gs.bottomLeftTip = { message, dismissDelay };
    window.render();
  }

  static clearThreadIdCommentId() {
    let args = { ...window.ARGS };
    delete args.tId;
    delete args.cId;
    window.setARGS(args, true);
  }

  static reloadWindowToCollectionId(collectionId) {
    window.setARGS({ activeTab: 'COLLECTIONS', collectionId: collectionId }, true);
    location.reload();
  }

  static setDialog(dialog) {
    let args = { ...window.ARGS };
    let replace = false;
    if (dialog) {
      args.dialog = dialog;
    } else {
      delete args.dialog;
      replace = true;
    }

    ViewUtil.setARGS(args, replace);
  }

  static getDialog() {
    return window.ARGS.dialog;
  }

  static getActiveView() {
    if (ViewUtil.isAssetTabActive() && ViewUtil.isAssetListViewActive()) {
      return View.ASSET_LIST;
    }
    if (ViewUtil.isAssetTabActive() && ViewUtil.isAssetDetailViewActive()) {
      return View.ASSET_DETAIL;
    }
    if (ViewUtil.isCollectionListViewActive()) {
      return View.COLLECTION_LIST;
    }
    if (ViewUtil.isCollectionDetailAssetListViewActive()) {
      return View.COLLECTION_DETAIL_ASSET_LIST;
    }
    if (ViewUtil.isCollectionDetailAssetDetailViewActive()) {
      return View.COLLECTION_DETAIL_ASSET_DETAIL;
    }

    return View.HOME;
  }

  static getReportId() {
    const { reportId } = window.ARGS;
    return reportId ? parseInt(reportId, 10) : null;
  }

  static setTitle() {
    const args = window.ARGS || {};
    const { activeTab } = args;
    const fuid = ViewUtil.getFuid();
    const collectionId = ViewUtil.getCollectionIdFromArgs();
    const tokens = [];

    if (activeTab === 'ASSETS') {
      if (fuid) {
        const asset = window.gs.selectedAssets.length === 1 ? window.gs.selectedAssets[0] : null;
        if (asset && asset.getFuid() === fuid) {
          tokens.push(asset.getName());
        }
      } else {
        tokens.push(t('ASSETS'));
      }
    } else if (activeTab === 'COLLECTIONS') {
      if (collectionId) {
        const collections = Store.get(COLLECTIONS) || [];
        const collection: ZmCollection = collections.find((c) => c.getCollectionId() === collectionId);
        if (collection) {
          if (fuid) {
            const asset: RepoItem = Store.get(COLLECTION_ASSETS).find((a) => a.getFuid() === fuid);
            if (asset) {
              tokens.push(asset.getName());
            }
          }
          tokens.push(collection.getName());
        }
      } else {
        tokens.push(t('COLLECTIONS'));
      }
    } else if (activeTab === 'SEARCH') {
      tokens.push(t('SEARCH'));
    } else if (activeTab === 'HOME') {
      tokens.push(t('HOME'));
    } else if (activeTab === TABS.EXTERNAL_FILES.KEY) {
      tokens.push(t(TABS.EXTERNAL_FILES.KEY));
    }

    tokens.push(window.gs.companyName);

    const title = tokens.join(' - ');
    debug('[setTitle()] updating page title to ', title);
    document.title = title;
  }

  static setAdvancedSearchQuery(data: Object) {
    Store.put(Q, data);
  }

  static getAdvancedSearchQuery() {
    try {
      return Store.get(Q);
    } catch (err) {
      return null;
    }
  }

  static getPwd(args: ?Object): number[] {
    args = args || window.ARGS;
    try {
      return _.compact(ViewUtil._pwdStringToArray(args.pwd || '').map((s) => parseInt(s, 10)));
    } catch (err) {
      return [];
    }
  }

  static setPwd(pwd: number[]) {
    debug('[setPwd( %O )]', pwd);
    Store.put(ASSETLIST_PAGE_NUMBER, 0);
    if (pwd) {
      ViewUtil.setARGS({ pwd: ViewUtil._pwdArrayToString(pwd) });
    } else {
      const args = { ...window.ARGS };
      delete args.pwd;
      ViewUtil.setARGS(args, true);
    }

    Store.put(PWD, ViewUtil.getPwd());
  }

  static pushPwd(fuid: number, append: boolean) {
    let pwd;
    if (append) {
      const currentPwd = ViewUtil.getPwd() || [];
      pwd = currentPwd.concat(fuid);
    } else {
      pwd = [fuid];
    }

    ViewUtil.setPwd(pwd);
  }

  static popPwd(index: number) {
    const pwd = ViewUtil.getPwd().slice(0, index + 1);
    ViewUtil.setPwd(pwd);
  }

  static _pwdArrayToString(pwd: number[]) {
    return pwd.join('.');
  }

  static _pwdStringToArray(pwd: string) {
    return pwd.split('.');
  }

  /**
   * Check Asset tab Show root Directory (Projects Dir.)
   * @return {Boolean}
   */
  static isAssetRootDir() {
    if (this.isAssetTabActive()) {
      if (Store.get(PWD).length === 0) {
        return false;
      }
    }
    return true;
  }

  static setTreeView(treeView: boolean) {
    const args = { ...window.ARGS };
    if (treeView) {
      delete args.tv;
    } else {
      args.tv = 0;
    }
    ViewUtil.setARGS(args, true);
  }

  static setFolderNavView(folderNavView: boolean) {
    const args = { ...window.ARGS };
    if (folderNavView) {
      delete args.fnv;
      delete args.tv;
    } else {
      args.fnv = 0;
    }
    console.log(args);
    ViewUtil.setARGS(args, true);
  }

  static getTreeView() {
    const args = { ...window.ARGS };
    if (args.tv) {
      return false;
    } else {
      return true;
    }
  }

  static isAllFileSearchMode() {
    if (!Store.get(SHOW_FOLDERNAV_VIEW)) return true;
    if (Store.get(ALL_FILES_OPTION) === 'SHOW_ALL_FILES') return true;
    const globalSearchMode = Store.get(GLOBAL_SEARCH_INFO).active;
    if (globalSearchMode) return true;
    return false;
  }

  static getFolderNavView() {
    const args = { ...window.ARGS };
    if (args.fnv) {
      return false;
    } else {
      return true;
    }
  }
}

//Get All Params from URL like &THEME=DARK
export const getParamsFromUrl = function(url) {
  var queryString = url ? url.split('?')[1] : window.location.search.slice(1);
  var obj = {};
  if (queryString) {
    queryString = queryString.split('#')[0];
    var arr = queryString.split('&');
    for (var i = 0; i < arr.length; i++) {
      var a = arr[i].split('=');
      var paramName = a[0];
      var paramValue = typeof a[1] === 'undefined' ? true : a[1];
      paramName = paramName.toLowerCase();
      if (typeof paramValue === 'string') paramValue = paramValue.toLowerCase();
      if (paramName.match(/\[(\d+)?\]$/)) {
        var key = paramName.replace(/\[(\d+)?\]/, '');
        if (!obj[key]) obj[key] = [];
        if (paramName.match(/\[\d+\]$/)) {
          var index = /\[(\d+)\]/.exec(paramName)[1];
          obj[key][index] = paramValue;
        } else {
          obj[key].push(paramValue);
        }
      } else {
        if (!obj[paramName]) {
          obj[paramName] = paramValue;
        } else if (obj[paramName] && typeof obj[paramName] === 'string') {
          obj[paramName] = [obj[paramName]];
          obj[paramName].push(paramValue);
        } else {
          obj[paramName].push(paramValue);
        }
      }
    }
  }
  return obj;
};

//For Get Search mode or AllFile view Mode


export const getUploadPath = function(asset?: RepoItem) {
  let projectName = '';
  let uploadPath = '';
  const breadCrumAssets = Store.get(BREADCRUMB_ASSETS);
  const isFolderNavView = Store.get(SHOW_FOLDERNAV_VIEW);
  if (isFolderNavView && breadCrumAssets.length && !asset) {
    const projectRepoItem: RepoItem = _.head(breadCrumAssets);
    if (projectRepoItem) {
      projectName = projectRepoItem.getName();
      uploadPath = getRemotePathFromBreadcrumb();
    }
  } else if (isFolderNavView && asset) {
    projectName = asset.getName();
    uploadPath = asset.getRemotePath() + '/';
  }
  return { projectName, uploadPath };
};
